import { combineReducers } from 'redux'

import { isEncoded } from 'utils/strings'

import { SOURCE_VALUES } from 'containers/AwarenessCenter/WizardCampaign/steps/TrainingModuleStep/constants'
import { WORKDAYS_VALUES } from 'containers/Settings/General/constants'

import callForActionReducer from './CallForAction/reducer'
import callForActionFormReducer from './CallForActionForm/reducer'
import autonomousSchedulerReducer from './Campaigns/AutonomousScheduler/reducer'
import campaignReportReducer from './Campaigns/CampaignReport/reducer'
import campaignsReducer from './Campaigns/reducer'
import landingPageFormReducer from './LandingPageForm/reducer'
import landingPagesReducer from './LandingPages/reducer'
import scenariosReducer from './Scenarios/reducer'
import wizardCampaignEdit from './WizardCampaign/WizardCampaignEdit/reducer'
import { FETCH_CAMPAIGNS_SETTINGS } from './constants'

const campaignsSettingsInitialState = {
  data: {
    showQuickLaunchBTN: false,
    showQuickLaunchNotice: false,
    languages: [],
    contactEmail: undefined,
    bounceEmail: undefined,
    contactName: undefined,
    timezone: undefined,
    collectBrowser: false,
    sensitiveMode: false,
    workdays: WORKDAYS_VALUES.ALL_WEEK,
    hidePersonalData: false,
    sharedMailboxProfilesExists: false,
    maxCampaignsAllowed: undefined,
    maxCampaignsByLicence: undefined,
    maxCampaignMailsAllowed: undefined,
    maxCampaignsMailsByLicence: undefined,
    hasCustomLogo: false,
    allowedSendersForTrainingCampaigns: [],
    trainingContentVendors: [],
    hasPremiumEnabled: undefined,
    defaultPremiumVendor: undefined,
    allowExternalCampaigns: false,
    isGraphApiEnabled: false,
  },
  loading: false,
  error: null,
}

const campaignsSettingsReducer =
  () =>
  (state = campaignsSettingsInitialState, action) => {
    switch (action.type) {
      case FETCH_CAMPAIGNS_SETTINGS.INITIATE:
        return { ...state, loading: true, error: null }
      case FETCH_CAMPAIGNS_SETTINGS.SUCCESS: {
        const {
          showQuickLaunchBTN,
          showQuickLaunchNotice,
          languages,
          contactEmail,
          bounceEmail,
          contactName,
          timezone,
          collectBrowser,
          sensitiveMode,
          workdays,
          hidePersonalData,
          sharedMailboxProfilesExists,
          maxCampaignsAllowed,
          maxCampaignsByLicence,
          maxCampaignMailsAllowed,
          maxCampaignsMailsByLicence,
          hasCustomLogo,
          allowedSendersForTrainingCampaigns,
          trainingContentVendors,
          allowExternalCampaigns,
          isGraphApiEnabled,
          defaultCampaignFailedAlertContent,
          defaultTrainingReminderContentSimulation,
          defaultTrainingReminderContentTraining,
          showMSPAutonomousCampaignButton,
          managerNotificationState,
          defaultManagerNotificationBannerContent,
          defaultManagerReportEmailContent,
        } = action.payload.data

        // take the first active premium content source
        const activePremiumSource = Object.keys(trainingContentVendors).find(
          (key) =>
            trainingContentVendors[key].default &&
            trainingContentVendors[key].id !== SOURCE_VALUES.IRONSCALES
        )

        return {
          ...state,
          data: {
            showQuickLaunchBTN,
            showQuickLaunchNotice,
            languages,
            contactEmail,
            bounceEmail,
            contactName,
            timezone,
            collectBrowser,
            sensitiveMode,
            workdays,
            hidePersonalData,
            sharedMailboxProfilesExists,
            maxCampaignsAllowed,
            maxCampaignsByLicence,
            maxCampaignMailsAllowed,
            maxCampaignsMailsByLicence,
            hasCustomLogo,
            allowedSendersForTrainingCampaigns,
            trainingContentVendors,
            hasPremiumEnabled: !!activePremiumSource,
            defaultPremiumVendor: trainingContentVendors[activePremiumSource]?.id,
            allowExternalCampaigns,
            isGraphApiEnabled,
            defaultCampaignFailedAlertContent: decodeContent(defaultCampaignFailedAlertContent),
            defaultTrainingReminderContentSimulation: decodeContent(
              defaultTrainingReminderContentSimulation
            ),
            defaultTrainingReminderContentTraining: decodeContent(
              defaultTrainingReminderContentTraining
            ),
            showMSPAutonomousCampaignButton,
            managerNotificationState,
            defaultManagerNotificationBannerContent,
            defaultManagerReportEmailContent,
          },
          loading: false,
          error: null,
        }
      }
      case FETCH_CAMPAIGNS_SETTINGS.ERROR:
        return { ...state, loading: false, error: action.error }
      default:
        return state
    }
  }

export default function createAwarenessCenterReducer(asyncReducers) {
  return combineReducers({
    campaignsSettings: campaignsSettingsReducer(),
    campaigns: campaignsReducer(),
    scenarios: scenariosReducer(),
    landingPages: landingPagesReducer(),
    landingPageForm: landingPageFormReducer(),
    callForAction: callForActionReducer(),
    callForActionForm: callForActionFormReducer(),
    campaignReport: campaignReportReducer(),
    wizardCampaignEdit: wizardCampaignEdit(),
    autonomousScheduler: autonomousSchedulerReducer(),
    ...asyncReducers,
  })
}

function decodeContent(contentByLanguage) {
  const decodedContent = {}
  Object.keys(contentByLanguage).forEach((key) => {
    const body = isEncoded(contentByLanguage[key].body)
      ? decodeURIComponent(contentByLanguage[key].body)
      : contentByLanguage[key].body

    decodedContent[key] = {
      subject: contentByLanguage[key].subject,
      body,
    }
  })

  return decodedContent
}
